import { useEffect, useState } from 'react';

/**
 * usePointerCoarse
 *
 * Detects whether the primary input is a coarse pointer (e.g., touch devices).
 * Returns a boolean that updates on media query changes.
 */
export default function usePointerCoarse(): boolean {
  const [isCoarse, setIsCoarse] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') {
      return;
    }

    const mq = window.matchMedia('(pointer: coarse)');
    const apply = () => setIsCoarse(!!mq.matches);
    apply();

    const handler = () => apply();
    type MqExt = MediaQueryList & {
      addEventListener?: (type: 'change', listener: (ev: MediaQueryListEvent) => void) => void;
      removeEventListener?: (
        type: 'change',
        listener: (ev: MediaQueryListEvent) => void,
      ) => void;
      addListener?: (listener: (this: MediaQueryList, ev: MediaQueryListEvent) => void) => void;
      removeListener?: (listener: (this: MediaQueryList, ev: MediaQueryListEvent) => void) => void;
    };

    const mqExt = mq as MqExt;
    // Prefer modern API
    if (typeof mqExt.addEventListener === 'function') {
      mqExt.addEventListener('change', handler);
    } else if (typeof mqExt.addListener === 'function') {
      // Fallback for older Safari
      mqExt.addListener(handler);
    }

    return () => {
      if (typeof mqExt.removeEventListener === 'function') {
        mqExt.removeEventListener('change', handler);
      } else if (typeof mqExt.removeListener === 'function') {
        mqExt.removeListener(handler);
      }
    };
  }, []);

  return isCoarse;
}
